import { filterConstituents, filterConstituentsByName, uniqueLookupIds, countUniqueConstituents, type ConstituentCandidate } from "./filterConstituents";
// uniqueLookupIds still used in validateAltruExists below

export type AltruExistsResult = "found" | "not_found" | "ambiguous";

/**
 * Checks whether a constituent with the given email and phone exists in Altru.
 * - "found"     — exactly one individual (or duplicates sharing one lookup ID)
 * - "not_found" — no matching individual record
 * - "ambiguous" — multiple different people share the same email + phone
 * Throws if the search request fails (non-2xx), so callers can surface a retryable error.
 */
export async function validateAltruExists(email: string, phone: string): Promise<AltruExistsResult> {
  const params = new URLSearchParams({ email, phone });
  const res = await fetch(`/api/constituents/search?${params.toString()}`);
  if (!res.ok) {
    throw new Error('Unable to verify your account details. Please try again.');
  }
  const json = await res.json() as { success: boolean; data: ConstituentCandidate[] };
  if (!json.success || !Array.isArray(json.data)) return "not_found";

  const matched = filterConstituents(json.data, email, phone);
  if (matched.length === 0) return "not_found";

  const lookupIds = uniqueLookupIds(matched);
  return lookupIds.size === 1 ? "found" : "ambiguous";
}

/**
 * Checks whether a constituent with the given email, phone, first name, and last
 * name exists in Altru. Used as the third disambiguation step when email + phone
 * alone returns multiple different people.
 * - "found"     — exactly one individual after name filtering
 * - "not_found" — no matching record after name filtering
 * - "ambiguous" — still multiple different people even after name filtering
 * Throws if the search request fails (non-2xx), so callers can surface a retryable error.
 */
export async function validateAltruExistsByName(
  email: string,
  phone: string,
  firstName: string,
  lastName: string,
): Promise<AltruExistsResult> {
  const params = new URLSearchParams({ email, phone });
  const res = await fetch(`/api/constituents/search?${params.toString()}`);
  if (!res.ok) {
    throw new Error("Unable to verify your account details. Please try again.");
  }
  const json = (await res.json()) as { success: boolean; data: ConstituentCandidate[] };
  if (!json.success || !Array.isArray(json.data)) return "not_found";

  const byPhone = filterConstituents(json.data, email, phone);
  const byName = filterConstituentsByName(byPhone, firstName, lastName);
  if (byName.length === 0) return "not_found";

  const lookupIds = uniqueLookupIds(byName);
  return lookupIds.size === 1 ? "found" : "ambiguous";
}

/**
 * Searches Altru for constituents matching the given email.
 * Returns the count of unique individual matches (0, 1, or more):
 * - Households and groups are excluded.
 * - Multiple records sharing the same lookup ID count as 1 person.
 * Throws if the search request fails (non-2xx), so callers can surface a retryable error.
 */
export async function countAltruConstituentsByEmail(email: string): Promise<number> {
  const params = new URLSearchParams({ email });
  const res = await fetch(`/api/constituents/search?${params.toString()}`);
  if (!res.ok) {
    throw new Error('Unable to verify your account details. Please try again.');
  }
  const json = await res.json() as { success: boolean; data: ConstituentCandidate[] };
  if (!json.success || !Array.isArray(json.data)) return 0;

  return countUniqueConstituents(json.data);
}
